import type { ShortsFilters } from '~/core/entities/property/filters';

import {
	applyHomeShortsRule,
	withImagesEvery,
	withImagesEveryForced,
	withListingSortRule
} from '~/composables/useShortsFilters/featuredVideosFilterRules';
import { useShortsLabels } from '~/composables/useShortsFilters/useShortsLabels';
import { useShortsFiltersStore } from '~/store/shortsFiltersStore';
import { removeEmptyValues } from '~/utils';
import { storeToRefs } from 'pinia';

export function useShortsFilters() {
	const store = useShortsFiltersStore();

	const lastViewedPropertyCookie = useCookie<string | undefined>(
		'last-viewed-property'
	);

	const {
		search,
		cities,
		regions,
		macroregions,
		states,
		minAskingPrice,
		maxAskingPrice,
		minArea,
		maxArea,
		propertyTypes,
		hasActiveFilters,
		currentFiltersObject
	} = storeToRefs(store);

	const local = store.local;

	const { selectionText, locationLabel } = useShortsLabels({
		cities,
		regions,
		macroregions,
		states
	});

	function clearLocation() {
		store.resetLocationFilterArrays();
	}

	const toShortsFiltersForApi = computed<ShortsFilters>(() =>
		applyHomeShortsRule(
			store.toShortsFilters(),
			hasActiveFilters.value,
			lastViewedPropertyCookie.value
		)
	);

	const toShortsFeedFeaturedVideosFilters = computed<ShortsFilters>(() =>
		withImagesEvery(toShortsFiltersForApi.value)
	);

	const toShortsListingFeaturedVideosFilters = computed<ShortsFilters>(
		() =>
			removeEmptyValues(
				withImagesEveryForced(withListingSortRule(toShortsFiltersForApi.value))
			) as ShortsFilters
	);

	// Preview usa local (estado de edição do sidebar), não os refs de rota
	const toPreviewFilters = computed<ShortsFilters>(() =>
		store.toPreviewFilters()
	);

	return {
		search,
		locationLabel,
		selectionText,
		cities,
		regions,
		macroregions,
		states,
		minAskingPrice,
		maxAskingPrice,
		minArea,
		maxArea,
		propertyTypes,
		hasActiveFilters,
		currentFiltersObject,
		clearFilters: store.clearFilters,
		clearLocation,
		local,
		syncLocalFromQuery: store.syncLocalFromQuery,
		applySidebarFilters: store.applySidebarFilters,
		toShortsFiltersForApi,
		toShortsFeedFeaturedVideosFilters,
		toShortsListingFeaturedVideosFilters,
		toPreviewFilters
	};
}
